interface LogEntry {
    level: 'info' | 'warn' | 'error' | 'debug';
    message: string;
    context?: string;
    data?: any;
    timestamp?: string;
}

class Logger {
    private formatLog(entry: LogEntry): string {
        const timestamp = entry.timestamp || new Date().toISOString();
        const logData = {
            time: timestamp,
            level: entry.level.toUpperCase(),
            message: entry.message,
            ...(entry.context && { context: entry.context }),
            ...(entry.data && { data: entry.data }),
        };

        // Always use structured JSON logging for consistency
        return JSON.stringify(logData);
    }

    private output(entry: LogEntry): void {
        const formattedLog = this.formatLog(entry);

        // Force output directly to stdout/stderr to bypass Next.js suppression
        try {
            switch (entry.level) {
                case 'error':
                    console.error(formattedLog);
                    break;
                case 'warn':
                    console.warn(formattedLog);
                    break;
                case 'info':
                case 'debug':
                default:
                    console.log(formattedLog);
                    break;
            }
        } catch (err) {
            console.error('Logger failed:', err);
            console.log(formattedLog);
        }
    }

    info(message: string, context?: string, data?: any): void {
        this.output({ level: 'info', message, context, data });
    }

    warn(message: string, context?: string, data?: any): void {
        this.output({ level: 'warn', message, context, data });
    }

    error(message: string, context?: string, data?: any): void {
        this.output({ level: 'error', message, context, data });
    }

    debug(message: string, context?: string, data?: any): void {
        // Only log debug in development or if DEBUG env var is set to 'true'
        if (
            typeof process !== 'undefined' &&
            (process.env.NODE_ENV === 'development' ||
                process.env.DEBUG === 'true')
        ) {
            this.output({ level: 'debug', message, context, data });
        }
    }
}

export const logger = new Logger();
